/**
 * Pre-1.13 numeric block ids (+ 4-bit data) → modern block states, for old MCEdit/Schematica
 * `.schematic` files. Covers the vanilla 1.12 blocks with their common variants (wood types, colours,
 * stone types, facing of stairs/doors/torches…). Unknown ids become `legacy:block_<id>` (drawn as a
 * placeholder cube, like a block from a missing mod).
 */

const COLORS = ["white", "orange", "magenta", "light_blue", "yellow", "lime", "pink", "gray", "light_gray", "cyan", "purple", "blue", "brown", "green", "red", "black"];
const WOOD = ["oak", "spruce", "birch", "jungle", "acacia", "dark_oak"];
/** data 2..5 → facing (chests, furnaces, ladders, wall signs, dispensers 0..5) */
const FACE6 = ["down", "up", "north", "south", "west", "east"];
/** stairs: data & 3 */
const STAIR_FACE = ["east", "west", "south", "north"];
/** fence gates, beds, pumpkins: data & 3 */
const GATE_FACE = ["south", "west", "north", "east"];
/** doors (lower half): data & 3 */
const DOOR_FACE = ["east", "south", "west", "north"];
/** trapdoors: data & 3 */
const TRAP_FACE = ["north", "south", "west", "east"];
/** torches: data 1..5 */
const TORCH_FACE = ["", "east", "west", "south", "north"];
const RAIL_SHAPE = ["north_south", "east_west", "ascending_east", "ascending_west", "ascending_north", "ascending_south", "south_east", "south_west", "north_west", "north_east"];
const SLAB1 = ["smooth_stone", "sandstone", "oak", "cobblestone", "brick", "stone_brick", "nether_brick", "quartz"];
const FLOWERS = ["poppy", "blue_orchid", "allium", "azure_bluet", "red_tulip", "orange_tulip", "white_tulip", "pink_tulip", "oxeye_daisy"];
const TALL = ["sunflower", "lilac", "tall_grass", "large_fern", "rose_bush", "peony"];

type Conv = (data: number) => string;
const s = (name: string, props?: Record<string, string | number | boolean>): string => {
  const ks = props ? Object.keys(props).sort() : [];
  return `minecraft:${name}${ks.length ? `[${ks.map((k) => `${k}=${props![k]}`).join(",")}]` : ""}`;
};
const pick = (names: string[], fallback = names[0]) => (d: number) => names[d] ?? fallback;
const plain = (name: string): Conv => () => s(name);
const variants = (names: string[]): Conv => (d) => s(pick(names)(d & 15));
const stairs = (name: string): Conv => (d) => s(name, { facing: STAIR_FACE[d & 3], half: d & 4 ? "top" : "bottom", shape: "straight" });
const facing6 = (name: string, extra?: Record<string, string>): Conv => (d) => s(name, { facing: FACE6[d & 7] ?? "north", ...extra });
const horiz = (name: string, extra?: Record<string, string>): Conv => (d) => s(name, { facing: FACE6[d & 7] === "down" || FACE6[d & 7] === "up" || !FACE6[d & 7] ? "north" : FACE6[d & 7], ...extra });
const colored = (suffix: string): Conv => (d) => s(`${COLORS[d & 15]}_${suffix}`);
const gate = (name: string): Conv => (d) => s(name, { facing: GATE_FACE[d & 3], open: !!(d & 4), in_wall: false, powered: false });
const trapdoor = (name: string): Conv => (d) => s(name, { facing: TRAP_FACE[d & 3], open: !!(d & 4), half: d & 8 ? "top" : "bottom" });
const log = (types: string[]): Conv => (d) => {
  const t = types[d & 3] ?? types[0];
  const axis = (d >> 2) & 3;
  return axis === 3 ? s(`${t}_wood`, { axis: "y" }) : s(`${t}_log`, { axis: ["y", "x", "z"][axis] });
};
const slab = (types: string[], dbl: boolean): Conv => (d) => {
  const t = types[d & 7] ?? types[0];
  // "smooth" double slabs (data 8+) are the full block
  if (dbl && d & 8 && (t === "smooth_stone" || t === "sandstone" || t === "quartz")) return s(t === "smooth_stone" ? "smooth_stone" : `smooth_${t}`);
  return s(`${t}_slab`, { type: dbl ? "double" : d & 8 ? "top" : "bottom" });
};
const torch = (name: string, wall: string, extra?: Record<string, boolean>): Conv => (d) => (d >= 1 && d <= 4 ? s(wall, { facing: TORCH_FACE[d], ...extra }) : s(name, extra));

/** id → converter (index = legacy id) */
const TABLE: (Conv | undefined)[] = [];
const def = (id: number, c: Conv | string) => { TABLE[id] = typeof c === "string" ? plain(c) : c; };

def(0, "air");
def(1, variants(["stone", "granite", "polished_granite", "diorite", "polished_diorite", "andesite", "polished_andesite"]));
def(2, (_) => s("grass_block", { snowy: false }));
def(3, variants(["dirt", "coarse_dirt", "podzol"]));
def(4, "cobblestone");
def(5, (d) => s(`${WOOD[d & 7] ?? "oak"}_planks`));
def(6, (d) => s(`${WOOD[d & 7] ?? "oak"}_sapling`));
def(7, "bedrock");
def(8, (d) => s("water", { level: d & 15 }));
def(9, (d) => s("water", { level: d & 15 }));
def(10, (d) => s("lava", { level: d & 15 }));
def(11, (d) => s("lava", { level: d & 15 }));
def(12, variants(["sand", "red_sand"]));
def(13, "gravel");
def(14, "gold_ore");
def(15, "iron_ore");
def(16, "coal_ore");
def(17, log(["oak", "spruce", "birch", "jungle"]));
def(18, (d) => s(`${["oak", "spruce", "birch", "jungle"][d & 3]}_leaves`, { persistent: true }));
def(19, variants(["sponge", "wet_sponge"]));
def(20, "glass");
def(21, "lapis_ore");
def(22, "lapis_block");
def(23, facing6("dispenser"));
def(24, variants(["sandstone", "chiseled_sandstone", "cut_sandstone"]));
def(25, "note_block");
def(26, (d) => s("red_bed", { facing: GATE_FACE[d & 3], part: d & 8 ? "head" : "foot" }));
def(27, (d) => s("powered_rail", { shape: RAIL_SHAPE[d & 7] ?? "north_south", powered: !!(d & 8) }));
def(28, (d) => s("detector_rail", { shape: RAIL_SHAPE[d & 7] ?? "north_south" }));
def(29, facing6("sticky_piston"));
def(30, "cobweb");
def(31, (d) => s(["dead_bush", "short_grass", "fern"][d & 3] ?? "short_grass"));
def(32, "dead_bush");
def(33, facing6("piston"));
def(34, facing6("piston_head"));
def(35, colored("wool"));
def(36, "air"); // moving piston
def(37, "dandelion");
def(38, (d) => s(FLOWERS[d] ?? "poppy"));
def(39, "brown_mushroom");
def(40, "red_mushroom");
def(41, "gold_block");
def(42, "iron_block");
def(43, slab(SLAB1, true));
def(44, slab(SLAB1, false));
def(45, "bricks");
def(46, "tnt");
def(47, "bookshelf");
def(48, "mossy_cobblestone");
def(49, "obsidian");
def(50, torch("torch", "wall_torch"));
def(51, "fire");
def(52, "spawner");
def(53, stairs("oak_stairs"));
def(54, horiz("chest"));
def(55, (d) => s("redstone_wire", { power: d & 15 }));
def(56, "diamond_ore");
def(57, "diamond_block");
def(58, "crafting_table");
def(59, (d) => s("wheat", { age: Math.min(7, d & 7) }));
def(60, "farmland");
def(61, horiz("furnace", { lit: "false" }));
def(62, horiz("furnace", { lit: "true" }));
def(63, (d) => s("oak_sign", { rotation: d & 15 }));
// doors: resolved with the half below/above (see legacyStates)
def(65, horiz("ladder"));
def(66, (d) => s("rail", { shape: RAIL_SHAPE[d] ?? "north_south" }));
def(67, stairs("cobblestone_stairs"));
def(68, horiz("oak_wall_sign"));
def(69, "lever");
def(70, "stone_pressure_plate");
def(72, "oak_pressure_plate");
def(73, "redstone_ore");
def(74, (_) => s("redstone_ore", { lit: true }));
def(75, torch("redstone_torch", "redstone_wall_torch", { lit: false }));
def(76, torch("redstone_torch", "redstone_wall_torch", { lit: true }));
def(77, "stone_button");
def(78, (d) => s("snow", { layers: (d & 7) + 1 }));
def(79, "ice");
def(80, "snow_block");
def(81, "cactus");
def(82, "clay");
def(83, "sugar_cane");
def(84, "jukebox");
def(85, "oak_fence");
def(86, (d) => s("carved_pumpkin", { facing: GATE_FACE[d & 3] }));
def(87, "netherrack");
def(88, "soul_sand");
def(89, "glowstone");
def(90, "nether_portal");
def(91, (d) => s("jack_o_lantern", { facing: GATE_FACE[d & 3] }));
def(92, "cake");
def(93, (d) => s("repeater", { facing: GATE_FACE[d & 3], delay: ((d >> 2) & 3) + 1 }));
def(94, (d) => s("repeater", { facing: GATE_FACE[d & 3], delay: ((d >> 2) & 3) + 1, powered: true }));
def(95, colored("stained_glass"));
def(96, trapdoor("oak_trapdoor"));
def(97, variants(["infested_stone", "infested_cobblestone", "infested_stone_bricks", "infested_mossy_stone_bricks", "infested_cracked_stone_bricks", "infested_chiseled_stone_bricks"]));
def(98, variants(["stone_bricks", "mossy_stone_bricks", "cracked_stone_bricks", "chiseled_stone_bricks"]));
def(99, (d) => s(d === 10 || d === 15 ? "mushroom_stem" : "brown_mushroom_block"));
def(100, (d) => s(d === 10 || d === 15 ? "mushroom_stem" : "red_mushroom_block"));
def(101, "iron_bars");
def(102, "glass_pane");
def(103, "melon");
def(104, "pumpkin_stem");
def(105, "melon_stem");
def(106, (d) => s("vine", { south: !!(d & 1), west: !!(d & 2), north: !!(d & 4), east: !!(d & 8), up: d === 0 }));
def(107, gate("oak_fence_gate"));
def(108, stairs("brick_stairs"));
def(109, stairs("stone_brick_stairs"));
def(110, "mycelium");
def(111, "lily_pad");
def(112, "nether_bricks");
def(113, "nether_brick_fence");
def(114, stairs("nether_brick_stairs"));
def(115, (d) => s("nether_wart", { age: Math.min(3, d & 3) }));
def(116, "enchanting_table");
def(117, "brewing_stand");
def(118, (d) => (d & 3 ? s("water_cauldron", { level: Math.min(3, d & 3) }) : s("cauldron")));
def(119, "end_portal");
def(120, (d) => s("end_portal_frame", { facing: GATE_FACE[d & 3], eye: !!(d & 4) }));
def(121, "end_stone");
def(122, "dragon_egg");
def(123, (_) => s("redstone_lamp", { lit: false }));
def(124, (_) => s("redstone_lamp", { lit: true }));
def(125, (d) => s(`${WOOD[d & 7] ?? "oak"}_slab`, { type: "double" }));
def(126, (d) => s(`${WOOD[d & 7] ?? "oak"}_slab`, { type: d & 8 ? "top" : "bottom" }));
def(127, (d) => s("cocoa", { facing: GATE_FACE[d & 3], age: Math.min(2, d >> 2) }));
def(128, stairs("sandstone_stairs"));
def(129, "emerald_ore");
def(130, horiz("ender_chest"));
def(131, "tripwire_hook");
def(132, "tripwire");
def(133, "emerald_block");
def(134, stairs("spruce_stairs"));
def(135, stairs("birch_stairs"));
def(136, stairs("jungle_stairs"));
def(137, "command_block");
def(138, "beacon");
def(139, variants(["cobblestone_wall", "mossy_cobblestone_wall"]));
def(140, "flower_pot");
def(141, (d) => s("carrots", { age: Math.min(7, d & 7) }));
def(142, (d) => s("potatoes", { age: Math.min(7, d & 7) }));
def(143, "oak_button");
def(144, "skeleton_skull");
def(145, (d) => s(["anvil", "chipped_anvil", "damaged_anvil"][(d >> 2) & 3] ?? "anvil", { facing: GATE_FACE[d & 3] }));
def(146, horiz("trapped_chest"));
def(147, "light_weighted_pressure_plate");
def(148, "heavy_weighted_pressure_plate");
def(149, (d) => s("comparator", { facing: GATE_FACE[d & 3] }));
def(150, (d) => s("comparator", { facing: GATE_FACE[d & 3], powered: true }));
def(151, "daylight_detector");
def(152, "redstone_block");
def(153, "nether_quartz_ore");
def(154, (d) => s("hopper", { facing: FACE6[d & 7] === "up" ? "down" : FACE6[d & 7] ?? "down" }));
def(155, (d) => (d >= 2 && d <= 4 ? s("quartz_pillar", { axis: ["y", "x", "z"][d - 2] }) : s(d === 1 ? "chiseled_quartz_block" : "quartz_block")));
def(156, stairs("quartz_stairs"));
def(157, (d) => s("activator_rail", { shape: RAIL_SHAPE[d & 7] ?? "north_south", powered: !!(d & 8) }));
def(158, facing6("dropper"));
def(159, colored("terracotta"));
def(160, colored("stained_glass_pane"));
def(161, (d) => s(`${["acacia", "dark_oak"][d & 1]}_leaves`, { persistent: true }));
def(162, log(["acacia", "dark_oak", "acacia", "dark_oak"]));
def(163, stairs("acacia_stairs"));
def(164, stairs("dark_oak_stairs"));
def(165, "slime_block");
def(166, "barrier");
def(167, trapdoor("iron_trapdoor"));
def(168, variants(["prismarine", "prismarine_bricks", "dark_prismarine"]));
def(169, "sea_lantern");
def(170, (d) => s("hay_block", { axis: ["y", "x", "z"][(d >> 2) & 3] ?? "y" }));
def(171, colored("carpet"));
def(172, "terracotta");
def(173, "coal_block");
def(174, "packed_ice");
// 175 double plants: resolved with the lower half (see legacyStates)
def(176, (d) => s("white_banner", { rotation: d & 15 }));
def(177, horiz("white_wall_banner"));
def(178, (_) => s("daylight_detector", { inverted: true }));
def(179, variants(["red_sandstone", "chiseled_red_sandstone", "cut_red_sandstone"]));
def(180, stairs("red_sandstone_stairs"));
def(181, (d) => (d & 8 ? s("smooth_red_sandstone") : s("red_sandstone_slab", { type: "double" })));
def(182, (d) => s("red_sandstone_slab", { type: d & 8 ? "top" : "bottom" }));
def(183, gate("spruce_fence_gate"));
def(184, gate("birch_fence_gate"));
def(185, gate("jungle_fence_gate"));
def(186, gate("dark_oak_fence_gate"));
def(187, gate("acacia_fence_gate"));
def(188, "spruce_fence");
def(189, "birch_fence");
def(190, "jungle_fence");
def(191, "dark_oak_fence");
def(192, "acacia_fence");
def(198, facing6("end_rod"));
def(199, "chorus_plant");
def(200, "chorus_flower");
def(201, "purpur_block");
def(202, (d) => s("purpur_pillar", { axis: ["y", "x", "z"][(d >> 2) & 3] ?? "y" }));
def(203, stairs("purpur_stairs"));
def(204, (_) => s("purpur_slab", { type: "double" }));
def(205, (d) => s("purpur_slab", { type: d & 8 ? "top" : "bottom" }));
def(206, "end_stone_bricks");
def(207, (d) => s("beetroots", { age: Math.min(3, d & 3) }));
def(208, "dirt_path");
def(209, "end_gateway");
def(210, "repeating_command_block");
def(211, "chain_command_block");
def(212, "frosted_ice");
def(213, "magma_block");
def(214, "nether_wart_block");
def(215, "red_nether_bricks");
def(216, (d) => s("bone_block", { axis: ["y", "x", "z"][(d >> 2) & 3] ?? "y" }));
def(217, "air"); // structure void
def(218, facing6("observer"));
for (let i = 0; i < 16; i++) {
  const c = COLORS[i];
  def(219 + i, facing6(`${c}_shulker_box`));
  def(235 + i, (d) => s(`${c}_glazed_terracotta`, { facing: GATE_FACE[d & 3] }));
}
def(251, colored("concrete"));
def(252, colored("concrete_powder"));
def(255, "structure_block");

const DOORS: Record<number, string> = { 64: "oak_door", 71: "iron_door", 193: "spruce_door", 194: "birch_door", 195: "jungle_door", 196: "acacia_door", 197: "dark_oak_door" };

/** 1.12 block names (as used in Schematica's id mapping) → legacy id */
export const LEGACY_NAME_IDS: Record<string, number> = {
  air: 0, stone: 1, grass: 2, dirt: 3, cobblestone: 4, planks: 5, sapling: 6, bedrock: 7, flowing_water: 8, water: 9, flowing_lava: 10, lava: 11,
  sand: 12, gravel: 13, gold_ore: 14, iron_ore: 15, coal_ore: 16, log: 17, leaves: 18, sponge: 19, glass: 20, lapis_ore: 21, lapis_block: 22,
  dispenser: 23, sandstone: 24, noteblock: 25, bed: 26, golden_rail: 27, detector_rail: 28, sticky_piston: 29, web: 30, tallgrass: 31, deadbush: 32,
  piston: 33, piston_head: 34, wool: 35, piston_extension: 36, yellow_flower: 37, red_flower: 38, brown_mushroom: 39, red_mushroom: 40,
  gold_block: 41, iron_block: 42, double_stone_slab: 43, stone_slab: 44, brick_block: 45, tnt: 46, bookshelf: 47, mossy_cobblestone: 48,
  obsidian: 49, torch: 50, fire: 51, mob_spawner: 52, oak_stairs: 53, chest: 54, redstone_wire: 55, diamond_ore: 56, diamond_block: 57,
  crafting_table: 58, wheat: 59, farmland: 60, furnace: 61, lit_furnace: 62, standing_sign: 63, wooden_door: 64, ladder: 65, rail: 66,
  stone_stairs: 67, wall_sign: 68, lever: 69, stone_pressure_plate: 70, iron_door: 71, wooden_pressure_plate: 72, redstone_ore: 73,
  lit_redstone_ore: 74, unlit_redstone_torch: 75, redstone_torch: 76, stone_button: 77, snow_layer: 78, ice: 79, snow: 80, cactus: 81,
  clay: 82, reeds: 83, jukebox: 84, fence: 85, pumpkin: 86, netherrack: 87, soul_sand: 88, glowstone: 89, portal: 90, lit_pumpkin: 91,
  cake: 92, unpowered_repeater: 93, powered_repeater: 94, stained_glass: 95, trapdoor: 96, monster_egg: 97, stonebrick: 98,
  brown_mushroom_block: 99, red_mushroom_block: 100, iron_bars: 101, glass_pane: 102, melon_block: 103, pumpkin_stem: 104, melon_stem: 105,
  vine: 106, fence_gate: 107, brick_stairs: 108, stone_brick_stairs: 109, mycelium: 110, waterlily: 111, nether_brick: 112,
  nether_brick_fence: 113, nether_brick_stairs: 114, nether_wart: 115, enchanting_table: 116, brewing_stand: 117, cauldron: 118,
  end_portal: 119, end_portal_frame: 120, end_stone: 121, dragon_egg: 122, redstone_lamp: 123, lit_redstone_lamp: 124,
  double_wooden_slab: 125, wooden_slab: 126, cocoa: 127, sandstone_stairs: 128, emerald_ore: 129, ender_chest: 130, tripwire_hook: 131,
  tripwire: 132, emerald_block: 133, spruce_stairs: 134, birch_stairs: 135, jungle_stairs: 136, command_block: 137, beacon: 138,
  cobblestone_wall: 139, flower_pot: 140, carrots: 141, potatoes: 142, wooden_button: 143, skull: 144, anvil: 145, trapped_chest: 146,
  light_weighted_pressure_plate: 147, heavy_weighted_pressure_plate: 148, unpowered_comparator: 149, powered_comparator: 150,
  daylight_detector: 151, redstone_block: 152, quartz_ore: 153, hopper: 154, quartz_block: 155, quartz_stairs: 156, activator_rail: 157,
  dropper: 158, stained_hardened_clay: 159, stained_glass_pane: 160, leaves2: 161, log2: 162, acacia_stairs: 163, dark_oak_stairs: 164,
  slime: 165, barrier: 166, iron_trapdoor: 167, prismarine: 168, sea_lantern: 169, hay_block: 170, carpet: 171, hardened_clay: 172,
  coal_block: 173, packed_ice: 174, double_plant: 175, standing_banner: 176, wall_banner: 177, daylight_detector_inverted: 178,
  red_sandstone: 179, red_sandstone_stairs: 180, double_stone_slab2: 181, stone_slab2: 182, spruce_fence_gate: 183, birch_fence_gate: 184,
  jungle_fence_gate: 185, dark_oak_fence_gate: 186, acacia_fence_gate: 187, spruce_fence: 188, birch_fence: 189, jungle_fence: 190,
  dark_oak_fence: 191, acacia_fence: 192, spruce_door: 193, birch_door: 194, jungle_door: 195, acacia_door: 196, dark_oak_door: 197,
  end_rod: 198, chorus_plant: 199, chorus_flower: 200, purpur_block: 201, purpur_pillar: 202, purpur_stairs: 203, purpur_double_slab: 204,
  purpur_slab: 205, end_bricks: 206, beetroots: 207, grass_path: 208, end_gateway: 209, repeating_command_block: 210,
  chain_command_block: 211, frosted_ice: 212, magma: 213, nether_wart_block: 214, red_nether_brick: 215, bone_block: 216,
  structure_void: 217, observer: 218, concrete: 251, concrete_powder: 252, structure_block: 255,
};

/** One legacy block (no neighbour context) → modern state key. */
export function legacyState(id: number, data: number): string {
  const c = TABLE[id];
  if (c) return c(data & 15);
  if (DOORS[id]) return s(DOORS[id], { facing: DOOR_FACE[data & 3], half: data & 8 ? "upper" : "lower", open: !!(data & 4), hinge: "left" });
  if (id === 175) return s(TALL[data & 7] ?? "tall_grass", { half: data & 8 ? "upper" : "lower" });
  return `legacy:block_${id}`;
}

/**
 * Convert a whole legacy block array. Doors and tall plants store half of their state in the other
 * half, so those look at the block below/above. `index = (y * length + z) * width + x`.
 * `extra`: ids that stand for a block name instead (Schematica's mapping for modded blocks).
 */
export function legacyStates(ids: Uint16Array, data: Uint8Array, width: number, length: number, extra?: Map<number, string>): { palette: string[]; blocks: Uint16Array | Uint32Array } {
  const layer = width * length;
  const map = new Map<string, number>([["minecraft:air", 0]]);
  const palette = ["minecraft:air"];
  const blocks = new Uint32Array(ids.length);
  const cache = new Map<number, number>(); // (id << 4 | data) → palette index, for blocks without neighbour rules
  const idx = (key: string) => {
    let i = map.get(key);
    if (i === undefined) { i = palette.length; palette.push(key); map.set(key, i); }
    return i;
  };
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (id === 0) continue;
    const d = data[i] & 15;
    // ids the file mapped to a (modded) block name: the name, without data
    const named = extra?.get(id);
    if (named !== undefined) { blocks[i] = idx(named); continue; }
    if (DOORS[id] || id === 175) {
      let key: string;
      if (DOORS[id]) {
        // lower: facing + open; upper: hinge (+ powered)
        const upper = !!(d & 8);
        const lo = upper ? (i >= layer && ids[i - layer] === id ? data[i - layer] & 15 : 0) : d;
        const up = upper ? d : i + layer < ids.length && ids[i + layer] === id ? data[i + layer] & 15 : 8;
        key = s(DOORS[id], { facing: DOOR_FACE[lo & 3], half: upper ? "upper" : "lower", open: !!(lo & 4), hinge: up & 1 ? "right" : "left" });
      } else {
        const upper = !!(d & 8);
        const lo = upper ? (i >= layer && ids[i - layer] === 175 ? data[i - layer] & 7 : 2) : d & 7;
        key = s(TALL[lo] ?? "tall_grass", { half: upper ? "upper" : "lower" });
      }
      blocks[i] = idx(key);
      continue;
    }
    const k = (id << 4) | d;
    let p = cache.get(k);
    if (p === undefined) {
      const key = legacyState(id, d);
      p = key === "minecraft:air" ? 0 : idx(key);
      cache.set(k, p);
    }
    blocks[i] = p;
  }
  return { palette, blocks: palette.length <= 65536 ? Uint16Array.from(blocks) : blocks };
}
